import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  CheckCircle,
  Event,
  Receipt,
  Science,
  Message,
  Delete,
  DoneAll
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { clientDashboardService } from '@services/clientDashboardService';

interface Notification {
  id: string;
  type?: string;
  channel?: string;
  title?: string;
  body?: string;
  message?: string;
  status?: string;
  created_at?: string;
  read_at: string | null;
  reference_type: string | null;
  reference_id: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const notificationIcons: Record<string, any> = {
  appointment_reminder: <Event />,
  appointment_confirmed: <CheckCircle />,
  appointment_cancelled: <Event />,
  lab_results_ready: <Science />,
  prescription_ready: <Science />,
  invoice_due: <Receipt />,
  invoice_overdue: <Receipt />,
  vaccination_due: <NotificationsActive />,
  message_received: <Message />,
  system: <Notifications />
};

const notificationColors: Record<string, string> = {
  appointment_reminder: 'primary.main',
  appointment_confirmed: 'success.main',
  appointment_cancelled: 'error.main',
  lab_results_ready: 'info.main',
  prescription_ready: 'success.main',
  invoice_due: 'warning.main',
  invoice_overdue: 'error.main',
  vaccination_due: 'warning.main',
  message_received: 'primary.main',
  system: 'default'
};

const MyNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientDashboardService.getNotifications();
      setNotifications(data);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Eroare la incarcarea notificarilor');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await clientDashboardService.markNotificationAsRead(id);
      await loadNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const getFilteredNotifications = () => {
    if (tabValue === 0) {
      return notifications;
    } else if (tabValue === 1) {
      return notifications.filter(n => !n.read_at);
    } else {
      return notifications.filter(n => n.read_at);
    }
  };

  const unreadCount = notifications.filter(n => !n.read_at).length;

  const getNotificationType = (notification: Notification) => notification.type || 'system';
  const getNotificationBody = (notification: Notification) => notification.body || notification.message || 'Notificare fara continut';
  const getNotificationTitle = (notification: Notification) => notification.title || 'Notificare';
  const getNotificationCreatedAt = (notification: Notification) => notification.created_at || new Date().toISOString();

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Notificari
        </Typography>
        {unreadCount > 0 && (
          <Chip
            icon={<NotificationsActive />}
            label={`${unreadCount} necitite`}
            color="primary"
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={`Toate (${notifications.length})`} />
            <Tab label={`Necitite (${unreadCount})`} />
            <Tab label={`Citite (${notifications.length - unreadCount})`} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={tabValue}>
          {error && notifications.length === 0 ? (
            <Alert severity="error">
              Notificarile nu au putut fi incarcate momentan. Empty state-ul nu mai mascheaza o eroare de API.
            </Alert>
          ) : getFilteredNotifications().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
              <Notifications sx={{ fontSize: { xs: 60, md: 80 }, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nu ai notificari{tabValue === 1 ? ' necitite' : tabValue === 2 ? ' citite' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Notificarile tale vor aparea aici
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {getFilteredNotifications().map((notif, idx) => (
                <React.Fragment key={notif.id}>
                  <ListItem
                    sx={{
                      bgcolor: notif.read_at ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    secondaryAction={
                      !notif.read_at && (
                        <IconButton
                          edge="end"
                          onClick={() => handleMarkAsRead(notif.id)}
                          title="Marcheaza ca citit"
                        >
                          <DoneAll />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: notificationColors[getNotificationType(notif)] || 'default' }}>
                        {notificationIcons[getNotificationType(notif)] || <Notifications />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: notif.read_at ? 400 : 600 }}>
                          {getNotificationTitle(notif)}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {getNotificationBody(notif)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(getNotificationCreatedAt(notif)), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < getFilteredNotifications().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
      </Card>
    </Container>
  );
};

export default MyNotifications;

